import React, { useEffect, useMemo, useState } from 'react';
import MaterialReactTable from 'material-react-table';
import { DatabaseManager } from 'helpers/db';
import { ThemeProvider, createTheme } from '@mui/material/styles';

const dbManager = new DatabaseManager();

const tealTheme = createTheme({
  palette: {
    primary: {
      main: 'rgb(0,133,124)',
    },
  },
});

const ClusterInfoTable = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [columnVisibility, setColumnVisibility] = useState({});

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const res = await dbManager.getAllObjects(
          process.env.REACT_APP_DYNAMO_DB_SIZING_CACHE || 'roboproc-dev-sizing-cache'
        );
        setData(res.data || []);
        console.log('Sizing cache data:', res.data);
      } catch (e) {
        console.error('Error fetching sizing cache data:', e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const columns = useMemo(() => {
    if (data.length === 0) return [];

    // Get all unique keys from all records
    const allKeys = new Set();
    data.forEach(record => {
      Object.keys(record).forEach(key => allKeys.add(key));
    });

    // Filter out SWITCH_NAME completely - all variations
    const filteredKeys = Array.from(allKeys).filter(key => {
      const keyLower = key.toLowerCase().replace(/[\s_-]/g, '');
      return keyLower !== 'switchname';
    });

    // Generate columns dynamically
    return filteredKeys.map(key => {
      // Get unique values for filter options
      const uniqueValues = Array.from(
        new Set(data.map(d => d[key]).filter(val => val !== null && val !== undefined && val !== ''))
      );

      // Format header: First letter capital, rest lowercase
      const formatHeader = (str) => {
        const words = str.split('_').map(word => {
          if (word.length === 0) return '';
          return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
        });
        return words.join(' ');
      };

      const column = {
        accessorKey: key,
        header: formatHeader(key),
        size: 150,
        enableHiding: true,
      };

      // Add filter variant for columns with reasonable number of unique values
      if (uniqueValues.length > 0 && uniqueValues.length <= 20) {
        column.filterVariant = 'select';
        column.filterSelectOptions = uniqueValues.map(val => String(val)).sort();
      }

      // Special handling for wider columns
      if (key === 'Cluster' || key === 'CLUSTER_NAME') {
        column.size = 250;
      }

      return column;
    }).sort((a, b) => {
      // Define the display order for default visible columns
      const columnOrder = ['CLUSTER_NAME', 'FULL_NAME', 'HOST', 'Division', 'correct_size'];
      const aIndex = columnOrder.indexOf(a.accessorKey);
      const bIndex = columnOrder.indexOf(b.accessorKey);
      
      // If both columns are in the order list, sort by their position
      if (aIndex !== -1 && bIndex !== -1) {
        return aIndex - bIndex;
      }
      // If only a is in the order list, it comes first
      if (aIndex !== -1) return -1;
      // If only b is in the order list, it comes first
      if (bIndex !== -1) return 1;
      // Otherwise, sort alphabetically
      return a.header.localeCompare(b.header);
    });
  }, [data]);

  // Dynamic initial state for column visibility
  const getInitialColumnVisibility = useMemo(() => {
    const visibility = {};
    
    // Define the 5 columns that should be visible by default
    const defaultVisibleColumns = [
      'CLUSTER_NAME', 'FULL_NAME', 'HOST', 'Division', 'correct_size'
    ];
    
    columns.forEach(column => {
      const key = column.accessorKey;
      
      // Show only the 5 specified columns by default, hide all others
      if (defaultVisibleColumns.includes(key)) {
        visibility[key] = true;
      } else {
        visibility[key] = false;
      }
    });
    
    console.log('Column visibility state:', visibility);
    return visibility;
  }, [columns]);

  // Set initial column visibility when columns change
  useEffect(() => {
    const initialVisibility = getInitialColumnVisibility;
    if (Object.keys(initialVisibility).length > 0) {
      setColumnVisibility(initialVisibility);
    }
  }, [getInitialColumnVisibility]);

  return (
    <div style={{
      padding: 40,
      backgroundColor: 'white',
      border: '3px solid #f6f6f6',
      borderRadius: 10,
    }}>
      <ThemeProvider theme={tealTheme}>
        <MaterialReactTable
          columns={columns}
          data={data}
          enableColumnFilters
          enableSorting
          enablePagination
          enableHiding
          state={{ 
            isLoading: loading,
            columnVisibility: columnVisibility
          }}
          onColumnVisibilityChange={setColumnVisibility}
          initialState={{
            sorting: [{ id: 'Cluster', desc: false }],
            pagination: { pageSize: 10 },
          }}
          muiTableHeadCellProps={{
            sx: {
              backgroundColor: 'rgb(0,133,124)',
              color: 'white',
              fontWeight: 'bold',
            },
          }}
          muiTablePaperProps={{
            sx: { boxShadow: 'none', border: '1px solid #ccc' },
          }}
          muiTableBodyCellProps={{
            sx: {
              overflow: 'visible',
            },
          }}
          muiColumnActionsButtonProps={{
            sx: {
              color: 'rgb(0,133,124)',
            },
          }}
          muiShowHideColumnsButtonProps={{
            sx: {
              color: 'rgb(0,133,124)',
            },
          }}
          muiToggleButtonProps={{
            sx: {
              '&.Mui-selected': {
                backgroundColor: 'rgb(0,133,124)',
                color: 'white',
              },
              '&.Mui-selected:hover': {
                backgroundColor: 'rgba(0,133,124,0.8)',
              },
            },
          }}
          muiShowHideColumnsMenuProps={{
            sx: {
              '& .MuiSwitch-switchBase.Mui-checked': {
                color: 'rgb(0,133,124) !important',
              },
              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                backgroundColor: 'rgb(0,133,124) !important',
                opacity: 1,
              },
              '& .MuiSwitch-track': {
                backgroundColor: '#b2dfdb !important',
                opacity: 1,
              },
              '& .MuiSwitch-thumb': {
                color: 'rgb(0,133,124) !important',
              },
              '& .MuiCheckbox-root': {
                color: 'rgb(0,133,124) !important',
              },
              '& .MuiCheckbox-root.Mui-checked': {
                color: 'rgb(0,133,124) !important',
              },
              '& .MuiFormControlLabel-label': {
                color: 'rgb(0,133,124) !important',
              },
              '& .MuiTypography-root': {
                color: 'rgb(0,133,124) !important',
              },
              '& .MuiButton-root': {
                color: 'rgb(0,133,124) !important',
              },
            },
          }}
          muiTableToolbarProps={{
            sx: {
              '& .MuiIconButton-root': {
                color: 'rgb(0,133,124)',
              },
              '& .MuiIconButton-root:hover': {
                backgroundColor: 'rgba(0,133,124,0.1)',
              },
            },
          }}
        />
      </ThemeProvider>
    </div>
  );
};

export default ClusterInfoTable;
